import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { API_CONFIG } from '../config/constants';

interface PaymentSession {
  sessionId: string;
  paymentAddress: string;
  amount: number;
  expiresAt: string;
  tokensToReceive: number;
  network: string;
  chainId?: number;
  tokenAddress?: string;
  tokenSymbol?: string;
  decimals?: number;
  amountBaseUnits?: string;
}

interface PaymentStatus {
  session_id: string;
  wallet_address: string;
  status: string;
  amount_usd: number;
  tokens_allocated: number;
  tx_hash?: string;
  completed_at?: string;
}

interface PaymentState {
  // Payment session
  paymentSession: PaymentSession | null;
  paymentStatus: PaymentStatus | null;

  // Session management
  isSessionExpired: boolean;
  timeUntilExpiry: number;
  isCreatingSession: boolean;

  // Polling
  isPolling: boolean;
  pollRetryCount: number;

  // Actions
  setPaymentSession: (session: PaymentSession | null) => void;
  setPaymentStatus: (status: PaymentStatus | null) => void;
  setSessionExpired: (expired: boolean) => void;
  setTimeUntilExpiry: (time: number) => void;
  setCreatingSession: (creating: boolean) => void;
  setPolling: (polling: boolean) => void;
  incrementPollRetryCount: () => void;
  resetPollRetryCount: () => void;

  // Async actions
  createPaymentSession: (params: {
    walletAddress: string;
    amountUsd: number;
    network: string;
    token?: string;
  }) => Promise<void>;
  checkPaymentStatus: (sessionId: string) => Promise<void>;

  // Cleanup
  resetPayment: () => void;
}

export const usePaymentStore = create<PaymentState>()(
  devtools(
    (set, get) => ({
      // Initial state
      paymentSession: null,
      paymentStatus: null,
      isSessionExpired: false,
      timeUntilExpiry: 0,
      isCreatingSession: false,
      isPolling: false,
      pollRetryCount: 0,

      // Actions
      setPaymentSession: (session) => set({ paymentSession: session }),
      setPaymentStatus: (status) => set({ paymentStatus: status }),
      setSessionExpired: (expired) => set({ isSessionExpired: expired }),
      setTimeUntilExpiry: (time) => set({ timeUntilExpiry: time }),
      setCreatingSession: (creating) => set({ isCreatingSession: creating }),
      setPolling: (polling) => set({ isPolling: polling }),
      incrementPollRetryCount: () => set((state) => ({ pollRetryCount: state.pollRetryCount + 1 })),
      resetPollRetryCount: () => set({ pollRetryCount: 0 }),

      // Async actions
      createPaymentSession: async (params) => {
        const { isCreatingSession } = get();
        if (isCreatingSession) return;

        set({ isCreatingSession: true });

        try {
          const requestBody: any = {
            wallet_address: params.walletAddress,
            amount_usd: params.amountUsd,
            network: params.network
          };

          // Only include token if specified (for ERC-20/token payments)
          if (params.token) {
            requestBody.token = params.token;
          }

          const response = await fetch(`${API_CONFIG.BASE_URL}/api/payment/create`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestBody)
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to create payment session');
          }

          const sessionData = await response.json();
          set({
            paymentSession: sessionData,
            isPolling: true,
            pollRetryCount: 0
          });

        } catch (error) {
          console.error('Payment session creation failed:', error);
          throw error;
        } finally {
          set({ isCreatingSession: false });
        }
      },

      checkPaymentStatus: async (sessionId) => {
        const { isSessionExpired, pollRetryCount } = get();

        if (isSessionExpired) {
          set({ isPolling: false, pollRetryCount: 0 });
          return;
        }

        try {
          const response = await fetch(`${API_CONFIG.BASE_URL}/api/payment/status/${sessionId}`);

          if (!response.ok) {
            get().incrementPollRetryCount();
            return;
          }

          const status = await response.json();
          set({ paymentStatus: status, pollRetryCount: 0 });

          if (['completed', 'completed_success'].includes(status.status)) {
            set({ isPolling: false, pollRetryCount: 0 });
          }

        } catch (error) {
          console.error('Payment status check failed:', error);
          get().incrementPollRetryCount();
        }
      },

      // Cleanup
      resetPayment: () => set({
        paymentSession: null,
        paymentStatus: null,
        isSessionExpired: false,
        timeUntilExpiry: 0,
        isCreatingSession: false,
        isPolling: false,
        pollRetryCount: 0
      })
    }),
    { name: 'payment-store' }
  )
);
